import { DestroyRef, ElementRef, inject } from '@angular/core';
import { Config, IonRouterOutlet, Platform } from '@ionic/angular/standalone';

/**
 * Apaga el swipe-back de iOS mientras esta página es la que se ve. Para las pantallas con
 * mapa nativo (TEC-352).
 *
 * Arrastrar el mapa (o hacerle pinch) con el dedo cerca del borde izquierdo arranca el
 * gesto de regresar, y el gesto dispara `ionViewWillLeave`. Si el usuario no lo completa,
 * Ionic NO emite `ionViewDidEnter` de vuelta (`utils/transition` sólo dispara los Did*
 * cuando la transición termina): la página destruía su mapa nativo en el WillLeave y se
 * quedaba en "Cargando mapa…" para siempre. Sin gesto no hay cancelación posible.
 *
 * Se apaga en `ionViewDidEnter` (un WillEnter llega también con un swipe-back cancelado
 * de la página de encima) y se restaura en `ionViewWillLeave`. Al cambiar de tab Ionic no
 * siempre emite el ciclo de vida, y el outlet de las tabs es uno solo: por eso también se
 * sigue `stackDidChange`, para no dejar el gesto apagado en las otras tabs.
 *
 * Restaura el valor por defecto de la plataforma (`swipeBackEnabled`, sólo iOS), no un
 * `true` fijo: en Android encenderlo activaba un gesto que el app nunca tuvo.
 *
 * Debe llamarse en contexto de inyección (inicializador de campo de la página).
 */
export function disableSwipeBackWhileShown(): void {
  const outlet = inject(IonRouterOutlet, { optional: true });
  if (!outlet) {
    return;
  }
  const host = inject(ElementRef).nativeElement as HTMLElement;
  const enabledByDefault = inject(Config).getBoolean('swipeBackEnabled', inject(Platform).is('ios'));

  const block = () => (outlet.swipeGesture = false);
  const restore = () => (outlet.swipeGesture = enabledByDefault);

  host.addEventListener('ionViewDidEnter', block);
  host.addEventListener('ionViewWillLeave', restore);
  const stackChanges = outlet.stackDidChange.subscribe(({ enteringView }) =>
    enteringView?.element === host ? block() : restore()
  );

  inject(DestroyRef).onDestroy(() => {
    host.removeEventListener('ionViewDidEnter', block);
    host.removeEventListener('ionViewWillLeave', restore);
    stackChanges.unsubscribe();
    // Un back completo destruye la página antes de que el outlet emita stackDidChange.
    restore();
  });
}
